import { useEffect, useRef } from 'react';
import { usePusher } from 'hooks/usePusher';
import {
  MENU_BUILD_FILE_UPLOADED,
  MENU_BUILD_FILE_PROCESSED,
  MENU_BUILD_MENU_CREATED,
  MENU_BUILD_MENU_INSERTED,
  MENU_BUILD_MENU_FAILED,
} from 'constants/menuManagement';

import type { BuildStatus } from 'containers/MenuUpload/types';

const BUILD_EVENTS = [
  MENU_BUILD_FILE_UPLOADED,
  MENU_BUILD_FILE_PROCESSED,
  MENU_BUILD_MENU_CREATED,
  MENU_BUILD_MENU_INSERTED,
  MENU_BUILD_MENU_FAILED,
];

const EVENT_TO_STATUS: Record<string, BuildStatus> = {
  [MENU_BUILD_FILE_UPLOADED]: 'file_uploaded',
  [MENU_BUILD_FILE_PROCESSED]: 'file_processed',
  [MENU_BUILD_MENU_CREATED]: 'menu_created',
  [MENU_BUILD_MENU_INSERTED]: 'menu_inserted',
  [MENU_BUILD_MENU_FAILED]: 'failed',
};

type UseMenuBuildNotificationsParams = {
  companyId: number | null;
  merchantId: number | null;
  onStatusChange: (status: BuildStatus) => void;
};

export const useMenuBuildNotifications = ({
  companyId,
  merchantId,
  onStatusChange,
}: UseMenuBuildNotificationsParams) => {
  // pusher is optional here because stories and tests render without the Pusher provider.
  const pusher = usePusher()?.pusher;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    if (!pusher || !companyId || !merchantId) return;

    const channelName = `menu-upload-${companyId}-${merchantId}`;
    const channel = pusher.subscribe(channelName);

    BUILD_EVENTS.forEach(event => {
      channel.bind(event, () => {
        onStatusChangeRef.current?.(EVENT_TO_STATUS[event]);
      });
    });

    return () => {
      BUILD_EVENTS.forEach(event => {
        channel.unbind(event);
      });
      pusher.unsubscribe(channelName);
    };
  }, [pusher, companyId, merchantId]);
};
